import { useCallback, useEffect } from 'react'

/**
 * Grid-wide keyboard navigation for the library Movies / Series tabs,
 * mirroring the desktop `useKeyboardNavigation` + `navigateGrid` combo:
 *
 *   ArrowLeft / ArrowRight:   move within the current row
 *   ArrowUp / ArrowDown:      move between rows (same column)
 *   Home / End:               first / last card in the current row
 *   Ctrl+Home / Ctrl+End:     first / last card in the whole grid
 *   Enter / Space:            activate the focused card (open detail)
 *
 * Listener is attached to `window` with `capture: true` so it catches
 * keypresses regardless of what has DOM focus, but bails out when the
 * active element is an input / textarea / contenteditable so typing
 * into the filter bar search field isn't hijacked. It also bails out
 * when `enabled` is false — callers should pass false while a modal,
 * detail overlay, or search sheet is open.
 *
 * The hook doesn't own the column count: it reads it back from the
 * `getColumns` callback on every keypress so the latest ResizeObserver
 * value from `useVirtualizedGrid` is always used, without needing to
 * re-bind the listener whenever the viewport resizes.
 */
export interface UseGridKeyboardNavParams {
  /** Whether the handler should be active. */
  enabled: boolean
  /** Total item count in the filtered list. */
  itemCount: number
  /** Currently focused index. `-1` means nothing is focused yet. */
  focusedIndex: number
  /** Setter called whenever the focused index changes. */
  setFocusedIndex: (index: number) => void
  /** Returns the current number of columns in the virtualized grid. */
  getColumns: () => number
  /** Fired on Enter/Space on the focused card — usually opens detail. */
  onActivate: (index: number) => void
}

export function useGridKeyboardNav({
  enabled,
  itemCount,
  focusedIndex,
  setFocusedIndex,
  getColumns,
  onActivate,
}: UseGridKeyboardNavParams): void {
  const clamp = useCallback(
    (idx: number) => {
      if (itemCount === 0) return -1
      if (idx < 0) return 0
      if (idx > itemCount - 1) return itemCount - 1
      return idx
    },
    [itemCount]
  )

  useEffect(() => {
    if (!enabled) return
    if (itemCount === 0) return

    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return
      }

      const columns = Math.max(1, getColumns())
      const current = focusedIndex < 0 ? 0 : focusedIndex

      switch (event.key) {
        case 'ArrowLeft': {
          event.preventDefault()
          // Stay within the current row: don't wrap off the left edge
          // into the previous row (matches desktop `navigateGrid`).
          const rowStart = Math.floor(current / columns) * columns
          const next = Math.max(rowStart, current - 1)
          setFocusedIndex(clamp(next))
          break
        }
        case 'ArrowRight': {
          event.preventDefault()
          const rowStart = Math.floor(current / columns) * columns
          const rowEnd = Math.min(itemCount - 1, rowStart + columns - 1)
          const next = Math.min(rowEnd, current + 1)
          setFocusedIndex(clamp(next))
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          const next = current - columns
          if (next >= 0) setFocusedIndex(clamp(next))
          break
        }
        case 'ArrowDown': {
          event.preventDefault()
          const next = current + columns
          if (next < itemCount) setFocusedIndex(clamp(next))
          else {
            // Allow going to the last item even if it sits in a
            // partially-filled final row.
            const lastRowStart = Math.floor((itemCount - 1) / columns) * columns
            if (current < lastRowStart) {
              setFocusedIndex(itemCount - 1)
            }
          }
          break
        }
        case 'Home': {
          event.preventDefault()
          if (event.ctrlKey || event.metaKey) {
            setFocusedIndex(0)
          } else {
            const rowStart = Math.floor(current / columns) * columns
            setFocusedIndex(rowStart)
          }
          break
        }
        case 'End': {
          event.preventDefault()
          if (event.ctrlKey || event.metaKey) {
            setFocusedIndex(itemCount - 1)
          } else {
            const rowStart = Math.floor(current / columns) * columns
            const rowEnd = Math.min(itemCount - 1, rowStart + columns - 1)
            setFocusedIndex(rowEnd)
          }
          break
        }
        case 'Enter':
        case ' ': {
          if (focusedIndex < 0) return
          event.preventDefault()
          onActivate(focusedIndex)
          break
        }
      }
    }

    window.addEventListener('keydown', handler, { capture: true })
    return () =>
      window.removeEventListener('keydown', handler, { capture: true })
  }, [
    enabled,
    itemCount,
    focusedIndex,
    setFocusedIndex,
    getColumns,
    onActivate,
    clamp,
  ])
}
